import { service } from '@ember/service'
import Component from '@glimmer/component'
import { formatNumber } from 'teacher-ui-client/utils/giraffe/formatter'
import { colors } from '@colors'
import convertDateFormat from 'teacher-ui-client/utils/giraffe/convert-date-format'
import type { IntlService } from 'ember-intl'
import { ChartInfoTooltip } from 'teacher-ui-client/components/giraffe/chart-info-tooltip'
import { SummaryBoxColorScheme } from 'teacher-ui-client/components/giraffe/summary-box/themes'
import { LeftSplitChart } from 'teacher-ui-client/components/giraffe/left-split-chart'
import { SummaryBox } from 'teacher-ui-client/components/giraffe/summary-box'
import {
  SeriesColumn,
  type ChartSpacing,
  type SeriesColumnOptions,
} from 'teacher-ui-client/components/giraffe/primitives/series-column'
import type { LegendOptions, TitleOptions } from 'highcharts'
import { reportingLegend as legend } from 'teacher-ui-client/utils/giraffe/highcharts-config'
import type { StadiumEventsTotalsDataItem } from 'teacher-ui-client/utils/student-events/fetchers/stadium-events-totals'

interface Args {
  summaryTitle: string
  data: StadiumEventsTotalsDataItem
  grade: string
  tooltip: string
}

interface Signature {
  Element: HTMLDivElement
  Args: Args
}

export class StadiumEventsTotals extends Component<Signature> {
  @service intl!: IntlService

  readonly title: TitleOptions = { text: this.intl.t('reporting.chartTitles.stadiumEventsTotals') }

  readonly chartSpacing: ChartSpacing = [10, 10, 10, 10]

  readonly legend: LegendOptions = legend('left', 'top', 50, -20)

  readonly colorScheme: SummaryBoxColorScheme = SummaryBoxColorScheme.LightBlue

  get categories(): string[] {
    const months = this.args.data?.months.map((monthItem) => monthItem.month) ?? []
    return months.map((month) => convertDateFormat(month))
  }

  get summaryStats(): { label: string; value: string }[] {
    const summaryData = this.args.data.summary
    const total = summaryData.total_count
    const studentCount = summaryData.student_count
    const average = summaryData.student_average

    return [
      {
        label: this.intl.t('reporting.summaryTitles.stadiumEventsTotals.completed'),
        value: formatNumber(total, 1),
      },
      {
        label: this.intl.t('reporting.statBoxes.courseProgress.studentCount'),
        value: formatNumber(studentCount, 1),
      },
      {
        label: this.intl.t('reporting.statBoxes.courseProgress.perStudentCount'),
        value: formatNumber(average, 1),
      },
    ]
  }

  get columns(): SeriesColumnOptions[] {
    const { intl } = this
    const { months } = this.args.data

    return [
      {
        name: intl.t('reporting.charts.stadiumEvents.categories.spelling'),
        data: months.map((month) => month.spelling),
        color: colors.oceanyBlue300,
        type: 'column',
      },
      {
        name: intl.t('reporting.charts.stadiumEvents.categories.grammar'),
        data: months.map((month) => month.grammar),
        color: colors.oceanyBlue400,
        type: 'column',
      },
      {
        name: intl.t('reporting.charts.stadiumEvents.categories.vocabulary'),
        data: months.map((month) => month.vocabulary),
        color: colors.juicyOrange300,
        type: 'column',
      },
      {
        name: intl.t('reporting.charts.stadiumEvents.categories.usage'),
        data: months.map((month) => month.usage),
        color: colors.purplyPink300,
        type: 'column',
      },
    ]
  }

  <template>
    <div data-test-giraffe-stadium-events-totals class="relative h-full" ...attributes>
      <ChartInfoTooltip @tooltipContent={{@tooltip}} />
      <LeftSplitChart>
        <:left>
          <SummaryBox
            class="h-full"
            @title={{@summaryTitle}}
            @grade={{@grade}}
            @stats={{this.summaryStats}}
            @colorScheme={{this.colorScheme}}
          />
        </:left>
        <:right>
          <SeriesColumn
            @title={{this.title}}
            @data={{this.columns}}
            @categories={{this.categories}}
            @stackingStyle="normal"
            @legend={{this.legend}}
            @yAxisReversedStacks={{false}}
            @chartSpacing={{this.chartSpacing}}
          />
        </:right>
      </LeftSplitChart>
    </div>
  </template>
}

export default StadiumEventsTotals
